/*
  macAutoAddOwners.js
  - Adds a small global helper on window.__macAutoAddOwners that other code can call with a macData object.
  - This file is imported from the app entry so the helper is available without changing component files.
  - The helper implements resilient portfolio detection and owner extraction and uses existing services if available.
*/
import { sendCollaborators } from "../Components/Collaborators/helpers/sendCollaborators";
import { FetchUserProfileService } from "../services/fetchUserProfileService";
import { fetchToken } from "../services/fetchTokenService";
import { loginRequest } from "../auth/authConfig";

// Expose a function on window to be called with (macData, contextHelpers)
// contextHelpers is optional and can include: collaborators, setCollaborators, postmortemTeamId, currentPostmortemObject, instance, accounts
function derivePortfolioName(macData) {
  if (!macData) return "";
  if (macData.portFolioName) return macData.portFolioName;
  if (macData.platform && macData.platform.portfolio && macData.platform.portfolio.portfolioName)
    return macData.platform.portfolio.portfolioName;
  if (macData.platformName) return macData.platformName;
  return "";
}

function extractOwnerEmails(macData) {
  if (!macData) return [];

  // If the server provided an array of owner emails, prefer and return all of them
  if (Array.isArray(macData.ownerEmails) && macData.ownerEmails.length) {
    return Array.from(new Set(macData.ownerEmails.map((e) => String(e).toLowerCase())));
  }

  // Backwards compatibility: single owner email fields
  const topLevelEmail = macData.ownerEmail || macData.owner?.email || macData.ownerEmailAddress;
  if (topLevelEmail) return [String(topLevelEmail).toLowerCase()];

  // Try common locations first
  const candidates = [
    macData.owners,
    macData.data && macData.data.owners,
    macData.content && macData.content.owners,
    macData.result && macData.result.owners,
    macData.application && macData.application.owners,
  ];

  let owners = candidates.find((c) => Array.isArray(c) && c.length > 0);

  // Fallback: shallow recursive search for an 'owners' array anywhere in the object
  if (!owners) {
    const stack = [macData];
    while (stack.length) {
      const node = stack.pop();
      if (!node || typeof node !== 'object') continue;
      if (Array.isArray(node.owners) && node.owners.length) {
        owners = node.owners;
        break;
      }
      for (const k of Object.keys(node)) {
        try {
          const v = node[k];
          if (v && typeof v === 'object') stack.push(v);
        } catch (e) {
          // ignore
        }
      }
    }
  }

  if (!owners || !Array.isArray(owners)) return [];

  const emails = owners
    .map((o) => o && (o.email || o.mail || o.userPrincipalName || o.scimEmail || ""))
    .filter(Boolean)
    .map((e) => e.toLowerCase());
  return Array.from(new Set(emails));
}

// Try to resolve a person's email(s) by name using Microsoft Graph search API (best-effort)
async function resolveEmailsFromName(name, instance, accounts) {
  if (!name || !instance || !accounts) return [];
  try {
    const tokenResponse = await fetchToken(instance, loginRequest, accounts);
    const accessToken = tokenResponse && tokenResponse.accessToken;
    if (!accessToken) return [];

    const body = JSON.stringify({
      requests: [
        {
          entityTypes: ["person"],
          query: { queryString: `${name}` },
          from: 0,
          size: 5,
        },
      ],
    });

    const resp = await fetch("https://graph.microsoft.com/v1.0/search/query", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body,
    });

    if (!resp.ok) {
      safeLog('resolveEmailsFromName: graph search failed', resp.status);
      return [];
    }

    const data = await resp.json();
    // Defensive parsing: look for person hits under value[0].hitsContainers[].hits[].resource
    const emails = new Set();
    const containers = (data && data.value) || data.requests || null;
    // data.value is expected; try to traverse several shapes
    if (Array.isArray(data.value) && data.value.length) {
      data.value.forEach((container) => {
        const hitsContainers = container.hitsContainers || [];
        hitsContainers.forEach((hc) => {
          const hits = hc.hits || [];
          hits.forEach((h) => {
            const res = h.resource || h;
            if (res.mail) emails.add(res.mail.toLowerCase());
            if (res.userPrincipalName) emails.add(res.userPrincipalName.toLowerCase());
            if (res.scimEmail) emails.add(res.scimEmail.toLowerCase());
          });
        });
      });
    } else if (Array.isArray(data.hits) && data.hits.length) {
      data.hits.forEach((h) => {
        const res = h.resource || h;
        if (res.mail) emails.add(res.mail.toLowerCase());
        if (res.userPrincipalName) emails.add(res.userPrincipalName.toLowerCase());
      });
    }

    return Array.from(emails);
  } catch (e) {
    safeLog('resolveEmailsFromName error', e);
    return [];
  }
}

// Safe no-op logger
function safeLog(...args) {
  if (typeof console !== "undefined") console.log("macAutoAddOwners:", ...args);
}

// The exported helper
function register() {
  if (typeof window === "undefined") return;
  if (window.__macAutoAddOwners) return; // already registered

  window.__macAutoAddOwners = async (macData, contextHelpers = {}) => {
    try {
      // Destructure context up-front to avoid initialization/order bugs
      const {
        collaborators = [],
        setCollaborators,
        postmortemTeamId,
        currentPostmortemObject,
        instance,
        accounts,
        addCollaboratorsinTeamsChatNew,
      } = contextHelpers || {};

  // No portfolio filtering — apply for all MAC IDs per request
  // (previously we skipped non-APMT portfolios)

      let ownerEmails = extractOwnerEmails(macData);
      if (!ownerEmails.length) {
        safeLog("no owner emails found in direct fields, attempting name-resolution");
        // Try to resolve from common name fields
        const possibleName = macData.ownerName || macData.owner || macData.ownerDisplayName || macData.ownerFullName || macData.applicationOwner || null;
        if (possibleName && instance && accounts) {
          const resolved = await resolveEmailsFromName(possibleName, instance, accounts);
          if (resolved && resolved.length) {
            ownerEmails = resolved;
            safeLog('resolved owner emails from name', ownerEmails);
          }
        }

        if (!ownerEmails.length) {
          safeLog("no owner emails found after name-resolution");
          return;
        }
      }

      const newEmails = ownerEmails.filter((e) => !collaborators.some((c) => c.email === e));
      if (!newEmails.length) {
        safeLog("no new owners to add");
        return;
      }

      const newCollabs = newEmails.map((e) => ({ email: e, status: true }));
      
      // We need to handle the latest state properly
      // Store the result from setState callback
      let updatedList = null;

      if (typeof setCollaborators === "function") {
        // Use functional update to ensure we get the latest collaborators state
        setCollaborators((prevCollaborators) => {
          // Compute the new list based on actual current state
          updatedList = [...(prevCollaborators || []), ...newCollabs];
          safeLog("added owners (" + newCollabs.length + ")", newEmails);
          return updatedList;
        });
      }
      
      // Persist to backend - use the list we computed, or fall back to manual calc
      const listToSend = updatedList || [...(collaborators || []), ...newCollabs];
      
      if (postmortemTeamId && instance && accounts) {
        try {
          safeLog("persisting " + listToSend.length + " collaborators to backend");
          const result = sendCollaborators(listToSend, instance, loginRequest, accounts, postmortemTeamId);
          // Only await if it's a promise
          if (result && typeof result.then === 'function') {
            await result;
          }
        } catch (e) {
          safeLog("error persisting collaborators", e);
        }
      }

    // teams chat add if teams chat id available and teams service provided via contextHelpers
    const chatId = currentPostmortemObject?.teamsChatGroup?.chatConversationId;
      if (chatId) {
        // try to use provided addCollaborators handler (if passed)
        if (typeof addCollaboratorsinTeamsChatNew === "function") {
          newEmails.forEach((email) => addCollaboratorsinTeamsChatNew(email, chatId));
        } else {
          // fallback: best-effort add using provided MSAL instance/accounts via fetchToken
      if (instance && accounts) {
            try {
              // Acquire token and add each user to chat
              const tokenResponse = await fetchToken(instance, loginRequest, accounts);
              const accessToken = tokenResponse && tokenResponse.accessToken;
              if (accessToken) {
                newEmails.forEach((email) => {
                  try {
                    fetch(
                      "https://graph.microsoft.com/v1.0/chats/" + chatId + "/members",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${accessToken}`,
                        },
                        body: JSON.stringify({
                          chatType: "group",
                          topic: "Group chat title",
                          "@odata.type": "#microsoft.graph.aadUserConversationMember",
                          "user@odata.bind":
                            "https://graph.microsoft.com/v1.0/users/" + email,
                          visibleHistoryStartDateTime: "0001-01-01T00:00:00Z",
                          roles: ["owner"],
                        }),
                      }
                    )
                      .then((response) => response.json())
                      .then((data) => safeLog('teams add response', data))
                      .catch((error) => safeLog('teams add error', error));
                  } catch (e) {
                    safeLog('teams add individual failed', e);
                  }
                });
              }
            } catch (e) {
              safeLog('teams add failed', e);
            }
          } else {
            safeLog('no instance/accounts for teams add');
          }
        }
      }
      safeLog('added owners', newEmails);
    } catch (e) {
      safeLog('unexpected error', e);
    }
  };
}

// Register immediately when module is imported
register();

export default register;
